/**
 * ReportLibraryPanel — collapsible v2 left panel for the Reports page.
 *
 * Replaces the 16-tab horizontal strip in AnalyticsDashboard. Organizes
 * reports into a small number of categories, supports filter-as-you-type,
 * persists its collapsed state in localStorage, and syncs the active
 * report to a URL hash so individual reports are bookmarkable.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight, Search,
  BarChart3, Sparkles, ArrowLeftRight, Users, UserCircle, MessageSquare,
  Megaphone, Target, ListChecks, Database, ShieldCheck, Gift, Award, Video,
  HeartPulse, Send, Flame, Activity, FileSpreadsheet, ClipboardList } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface ReportItem {
  id: string;
  label: string;
  icon?: LucideIcon;
  /** Optional one-line description shown beneath the label on hover */
  hint?: string;
}

export interface ReportCategory {
  id: string;
  label: string;
  items: ReportItem[];
}

interface ReportLibraryPanelProps {
  categories: ReportCategory[];
  activeId: string;
  onSelect: (id: string) => void;
  /** Persistence key for collapsed state. Defaults to 'reports-panel-collapsed' */
  storageKey?: string;
}

const COLLAPSED_W = 'w-[52px]';
const EXPANDED_W = 'w-[260px]';

/**
 * Default report catalog matching today's 16 tabs, grouped into 5 sections.
 * Exported so AnalyticsDashboard can reuse it without redefining the IA.
 */
export const DEFAULT_REPORT_CATALOG: ReportCategory[] = [
  {
    id: 'overview',
    label: 'Overview',
    items: [
      { id: 'executive', label: 'Executive', icon: Sparkles, hint: 'Top-line KPIs across all areas' },
      { id: 'custom', label: 'Custom Reports', icon: BarChart3, hint: 'Build, save, and schedule reports' },
    ],
  },
  {
    id: 'members',
    label: 'Members',
    items: [
      { id: 'members', label: 'Members', icon: UserCircle },
      { id: 'enrichment', label: 'Member Enrichment', icon: Database },
      { id: 'consent', label: 'Consent Management', icon: ShieldCheck },
      { id: 'incentives', label: 'Incentives', icon: Gift },
    ],
  },
  {
    id: 'engagement',
    label: 'Engagement',
    items: [
      { id: 'communications', label: 'Communications', icon: MessageSquare },
      { id: 'campaign-progress', label: 'Campaign Progress', icon: Megaphone },
      { id: 'engagement-insights', label: 'Engagement Insights', icon: Target },
      { id: 'survey-results', label: 'Survey Results', icon: ClipboardList, hint: 'Live results of public-link surveys + CSV export' },
    ],
  },
  {
    id: 'operations',
    label: 'Operations',
    items: [
      { id: 'referrals', label: 'Referrals', icon: ArrowLeftRight },
      { id: 'programs', label: 'Programs', icon: ListChecks },
      { id: 'tasks', label: 'Tasks', icon: ListChecks },
      { id: 'provider-performance', label: 'Provider Performance', icon: Award },
      { id: 'telehealth', label: 'Telehealth', icon: Video },
    ],
  },
  {
    id: 'care',
    label: 'Care Management',
    items: [
      { id: 'ecm-insights', label: 'ECM Insights', icon: HeartPulse },
      { id: 'ecm-progress-notes', label: 'ECM Progress Notes', icon: ListChecks, hint: 'All members’ progress notes in one place + CSV export' },
      { id: 'sfhp-invoice', label: 'SFHP Invoice', icon: FileSpreadsheet, hint: 'The 54-column payer file in SFHP’s OASTD template' },
      { id: 'member-retention', label: 'Retention Intelligence', icon: HeartPulse, hint: 'Churn risk + ML predictions' },
      { id: 'environmental-impact', label: 'Environmental Impact', icon: Flame, hint: 'Disaster exposure × engagement by federation' },
    ],
  },
  {
    id: 'recovery',
    label: 'Disaster Case Management',
    items: [
      {
        id: 'dcm-overview',
        label: 'Case Overview',
        icon: Flame,
        hint: 'Active caseload, status mix, aging and per-case-manager load',
      },
      {
        id: 'dcm-activity',
        label: 'Team Activity',
        icon: Activity,
        hint: 'Recent notes, tasks and record changes by case manager',
      },
    ],
  },
  {
    id: 'delivery',
    label: 'Delivery',
    items: [
      { id: 'report-distribution', label: 'Report Distribution', icon: Send },
      { id: 'scheduled-reports', label: 'Scheduled Reports', icon: Send },
    ],
  },
];

const findCategoryFor = (catalog: ReportCategory[], id: string) =>
  catalog.find((c) => c.items.some((i) => i.id === id))?.id;

export const ReportLibraryPanel: React.FC<ReportLibraryPanelProps> = ({
  categories,
  activeId,
  onSelect,
  storageKey = 'reports-panel-collapsed',
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(storageKey) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, collapsed ? '1' : '0');
    } catch {
      /* noop */
    }
  }, [collapsed, storageKey]);

  const [query, setQuery] = useState('');
  const [openSections, setOpenSections] = useState<Record<string, boolean>>(() => {
    const activeCat = findCategoryFor(categories, activeId);
    const init: Record<string, boolean> = {};
    categories.forEach((c) => {
      init[c.id] = c.id === activeCat;
    });
    return init;
  });

  // Keep the active section open when activeId changes externally (hash routing)
  const lastActiveRef = useRef(activeId);
  useEffect(() => {
    if (activeId !== lastActiveRef.current) {
      const cat = findCategoryFor(categories, activeId);
      if (cat) setOpenSections((prev) => ({ ...prev, [cat]: true }));
      lastActiveRef.current = activeId;
    }
  }, [activeId, categories]);

  const filtered = useMemo(() => {
    if (!query.trim()) return categories;
    const q = query.toLowerCase();
    return categories
      .map((c) => ({
        ...c,
        items: c.items.filter(
          (i) => i.label.toLowerCase().includes(q) || (i.hint || '').toLowerCase().includes(q),
        ),
      }))
      .filter((c) => c.items.length > 0);
  }, [categories, query]);

  // When searching, expand every match
  useEffect(() => {
    if (!query.trim()) return;
    setOpenSections((prev) => {
      const next = { ...prev };
      filtered.forEach((c) => {
        next[c.id] = true;
      });
      return next;
    });
  }, [query, filtered]);

  if (collapsed) {
    return (
      <aside
        className={`${COLLAPSED_W} flex h-full flex-col border-r border-v2border bg-paper transition-[width] duration-150 print:hidden`}
        aria-label="Report library (collapsed)"
      >
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="flex h-11 items-center justify-center border-b border-v2border-muted text-ink-500 hover:bg-paper-2 hover:text-ink"
          aria-label="Expand report library"
          title="Expand report library"
        >
          <ChevronsRight className="h-4 w-4" />
        </button>
        <nav className="flex-1 overflow-y-auto py-2">
          {categories.flatMap((c) =>
            c.items.map((item) => {
              const Icon = item.icon || BarChart3;
              const isActive = item.id === activeId;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onSelect(item.id)}
                  title={item.label}
                  aria-current={isActive ? 'page' : undefined}
                  className={`mx-1 my-0.5 flex h-9 w-[44px] items-center justify-center rounded-md transition-colors ${
                    isActive
                      ? 'bg-v2accent/10 text-v2accent'
                      : 'text-ink-500 hover:bg-paper-2 hover:text-ink'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </button>
              );
            }),
          )}
        </nav>
      </aside>
    );
  }

  return (
    <aside
      className={`${EXPANDED_W} flex h-full flex-col border-r border-v2border bg-paper transition-[width] duration-150 print:hidden`}
      aria-label="Report library"
    >
      <div className="flex items-center gap-2 border-b border-v2border-muted px-3 py-2">
        <div className="text-[11px] font-medium uppercase tracking-[0.04em] text-ink-500">
          Reports
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => setCollapsed(true)}
          className="inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-500 hover:bg-paper-2 hover:text-ink"
          aria-label="Collapse report library"
          title="Collapse"
        >
          <ChevronsLeft className="h-4 w-4" />
        </button>
      </div>

      <div className="border-b border-v2border-muted px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter reports…"
            className="h-8 w-full rounded-md border border-v2border bg-paper pl-7 pr-2 text-sm text-ink placeholder:text-ink-500 focus:border-v2accent focus:outline-none focus:ring-1 focus:ring-v2accent/40"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-1">
        {filtered.length === 0 && (
          <div className="px-3 py-6 text-center text-xs text-ink-500">No reports match “{query}”.</div>
        )}
        {filtered.map((category) => {
          const open = !!openSections[category.id];
          return (
            <div key={category.id} className="px-1">
              <button
                type="button"
                onClick={() => setOpenSections((p) => ({ ...p, [category.id]: !p[category.id] }))}
                className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-ink-500 hover:text-ink"
                aria-expanded={open}
              >
                {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                <span>{category.label}</span>
              </button>
              {open && (
                <div className="mb-1 pl-2">
                  {category.items.map((item) => {
                    const Icon = item.icon || BarChart3;
                    const isActive = item.id === activeId;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onSelect(item.id)}
                        aria-current={isActive ? 'page' : undefined}
                        title={item.hint || item.label}
                        className={`mb-0.5 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                          isActive
                            ? 'bg-v2accent/10 font-medium text-v2accent'
                            : 'text-ink hover:bg-paper-2'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                        <span className="truncate">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </nav>
    </aside>
  );
};

export default ReportLibraryPanel;
